import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    keyframes,
    styled,
} from '@mui/material';
import {
    Rocket,
    Star,
    Science,
    SatelliteAlt as Satellite,
    Public,
    Brightness7,
    CloudQueue,
} from '@mui/icons-material';
import { ButtonYellow } from '../../components/buttons';
import { useQuiz } from './hooks';
import { getAssessmentEvaluationResult } from '../../store/quiz/quizBaseApi';
import { ScreenTypes } from '../../store/quiz/quizSlice';
import EvaluationErrorModal from '../../components/quiz/EvaluationError';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const orbit = keyframes`
  from { transform: rotate(0deg) translateX(150px) rotate(0deg); }
  to { transform: rotate(360deg) translateX(150px) rotate(-360deg); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.8); }
`;

const rocketLaunch = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-50px) rotate(5deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const cloudFloat = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100vw); }
`;

const SpaceObject = styled(Box)({
    position: 'absolute',
    color: '#fff',
    animation: `${float} 6s infinite ease-in-out`,
    '& .MuiSvgIcon-root': {
        fontSize: '3rem',
        filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.8))',
    },
});

const OrbitingSatellite = styled(Box)({
    position: 'absolute',
    animation: `${orbit} 20s infinite linear`,
    '& .MuiSvgIcon-root': {
        fontSize: '2.5rem',
        color: '#fff',
        filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.8))',
    },
});

const StarIcon = styled(Box)({
    position: 'absolute',
    animation: `${twinkle} 2s infinite ease-in-out`,
    '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
        color: '#fff',
    },
});

const Cloud = styled(Box)({
    position: 'absolute',
    animation: `${cloudFloat} 20s linear infinite`,
    '& .MuiSvgIcon-root': {
        fontSize: '4rem',
        color: 'rgba(255, 255, 255, 0.4)',
    },
});

const RocketWrapper = styled(Box)({
    animation: `${rocketLaunch} 3s infinite ease-in-out`,
    '& .MuiSvgIcon-root': {
        fontSize: '6rem',
        color: 'rgb(255, 213, 0)',
        filter: 'drop-shadow(0 0 20px rgba(255, 213, 0, 0.8))',
    },
});

const ProgressPlanet = styled(Box)({
    position: 'relative',
    width: 150,
    height: 150,
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

// Tolerate a handful of transient poll errors before abandoning evaluation — bailing
// after a single blip left the assessment summary unwritten (assessment_summary null).
const MAX_NETWORK_FAILURES = 4;

function ProgressContainer({ isPolling }) {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((prevProgress) => {
                let newProgress = prevProgress + 1;
                if (newProgress >= 100) {
                    if (!isPolling) {
                        clearInterval(timer);
                        return 100;
                    } else {
                        newProgress = 0;
                    }
                }
                return newProgress;
            });
        }, 100);

        return () => clearInterval(timer);
    }, []);

    return (
        <ProgressPlanet>
            <CircularProgress
                variant="determinate"
                value={progress}
                size={150}
                thickness={4}
                sx={{
                    color: 'rgb(255, 213, 0)',
                    position: 'absolute',
                    '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                        filter: 'drop-shadow(0 0 8px rgba(255, 213, 0, 0.6))',
                    },
                }}
            />
            <Typography
                variant="h4"
                sx={{
                    position: 'absolute',
                    color: '#fff',
                    fontWeight: 'bold',
                    textShadow: '0 0 12px rgba(255, 255, 255, 0.6)',
                }}
            >
                {progress}%
            </Typography>
        </ProgressPlanet>
    )
}

function Evalating() {
    const [isPolling, setIsPolling] = useState(true);
    const [errored, setErrored] = useState(false);
    const { evaluationId, setEvaluationResult, setScreen } = useQuiz();
    const interval = useRef(null);
    const networkFailures = useRef(0);

    const handleNextScreen = () => {
        setScreen(ScreenTypes.Complete);
    }

    const onRetryPolling = () => {
        networkFailures.current = 0;
        setIsPolling(true);
        setErrored(false);
    }

    useEffect(() => {
        console.log("polling starting at", Date.now(), "and with network failures count set to", networkFailures.current);
        console.log("polling is set to", isPolling, "and evaluation id is", evaluationId);
        if (!isPolling || !evaluationId || networkFailures.current >= MAX_NETWORK_FAILURES) return;

        const stopPolling = (response = {}) => {
            setIsPolling(false);

            if (response && Object.keys(response).length) {
                setEvaluationResult(response);
            } else {
                setErrored(true);
            }

            if (interval.current) {
                clearInterval(interval.current);
            }
        }

        const pollResults = async () => {
            try {
                const { data } = await getAssessmentEvaluationResult(evaluationId);
                // Guard against accepting a stale row: only settle on a result
                // that belongs to the attempt we're polling for (its id must
                // match the current evaluationId).
                if (data && Object.keys(data).length && (!data.id || data.id === evaluationId) && data.evaluated && Array.isArray(data.evaluationDetails) && data.evaluationDetails.length) {
                    stopPolling(data);
                }

            } catch (error) {
                console.error('Polling error:', error);
                if (networkFailures.current >= MAX_NETWORK_FAILURES) {
                    return stopPolling();
                }
                networkFailures.current += 1;
            }
        };

        interval.current = setInterval(pollResults, 2500);

        return () => clearInterval(interval.current);

    }, [isPolling, evaluationId]);

    return (
        <Box sx={{
            minHeight: 'calc(100vh - 130px)',
            background: 'linear-gradient(135deg, #0c0c3f 0%, #1a237e 50%, #311b92 100%)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            {/* Background Elements */}
            {[...Array(15)].map((_, i) => (
                <StarIcon
                    key={i}
                    sx={{
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${-Math.random() * 2}s`,
                    }}
                >
                    <Star sx={{ fontSize: `${Math.random() * 1 + 0.5}rem` }} />
                </StarIcon>
            ))}

            {[...Array(3)].map((_, i) => (
                <Cloud
                    key={i}
                    sx={{
                        top: `${20 + i * 30}%`,
                        animationDelay: `${-i * 7}s`,
                    }}
                >
                    <CloudQueue />
                </Cloud>
            ))}

            {/* Space Objects */}
            <SpaceObject sx={{ top: '15%', left: '15%' }}>
                <Science />
            </SpaceObject>
            <SpaceObject sx={{ top: '25%', right: '20%', animationDelay: '-2s' }}>
                <Public />
            </SpaceObject>
            <SpaceObject sx={{ bottom: '12%', left: '25%', animationDelay: '-4s' }}>
                <Brightness7 />
            </SpaceObject>

            {/* Orbiting Satellites */}
            {[...Array(2)].map((_, i) => (
                <OrbitingSatellite
                    key={i}
                    sx={{
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        animationDelay: `${-i * 10}s`,
                    }}
                >
                    <Satellite />
                </OrbitingSatellite>
            ))}

            {/* Main Content */}
            <Box sx={{
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
            }}>
                {isPolling ? (
                    <>
                        <RocketWrapper>
                            <Rocket />
                        </RocketWrapper>

                        <Typography variant="h3" sx={{
                            mb: 4,
                            fontWeight: 900,
                            color: '#fff',
                            textShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
                        }}>
                            Evaluating Your Results!
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <ProgressContainer isPolling={isPolling} />
                            <Public sx={{
                                fontSize: '5rem',
                                color: '#fff',
                                animation: `${float} 3s infinite ease-in-out`,
                                filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.6))',
                            }} />
                        </Box>

                        <Typography variant="h6" sx={{
                            color: '#fff',
                            opacity: 0.9,
                            maxWidth: '600px',
                            mx: 'auto',
                            mb: 3,
                            textShadow: '0 0 12px rgba(255, 255, 255, 0.4)',
                        }}>
                            We are currently evaluating your inputs and preparing your achievements...
                        </Typography>
                    </>
                ) : (
                    <>
                        <RocketWrapper>
                            <Rocket />
                        </RocketWrapper>

                        <Typography variant="h3" sx={{
                            mb: 3,
                            fontWeight: 900,
                            color: '#fff',
                            textShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
                        }}>
                            Evaluation Complete! 🚀
                        </Typography>

                        <Typography variant="h5" sx={{
                            mb: 4,
                            color: '#fff',
                            opacity: 0.9,
                            textShadow: '0 0 12px rgba(255, 255, 255, 0.4)',
                        }}>
                            Your evaluation results are ready...
                        </Typography>

                        <Box justifyContent={'center'} display={'flex'}>
                            <Box sx={{ width: '50%' }}>
                                <ButtonYellow
                                    buttonProps={{
                                        onClick: () => handleNextScreen(),
                                        style: { fontSize: '1.2rem' }
                                    }}
                                >
                                    View Your Score
                                </ButtonYellow>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
            <EvaluationErrorModal isOpen={errored} onSuccess={onRetryPolling} />
        </Box>
    );
}

export default Evalating;